/**
 * Insère une chaîne de caractères à une position spécifique déterminée par le nième point-virgule dans une chaîne originale.
 * @param originalString La chaîne originale dans laquelle nous souhaitons insérer.
 * @param insertString La chaîne à insérer dans la chaîne originale.
 * @param n Le numéro d'ordre du point-virgule après lequel l'insertion doit être faite.
 *          Par exemple, `n = 1` signifie insertion après le premier point-virgule.
 * @param overwrite Détermine si la chaîne doit être insérée en remplaçant les caractères existants (true) ou en les conservant (false).
 * @returns La nouvelle chaîne après insertion.
 * @example
 * // Exemple d'utilisation :
 * const originalString = "185,190;32;44,55;666;"
 * const insertString = "10,99,420"
 * const n = 2
 * result = 185,190;10,99,420;44,55;666;
 */
export function insertStringAtNthComma(originalString, insertString, n, overwrite = false) {
    let commaCount = 0;
    let startInsertPosition = -1;
    let endInsertPosition = -1;
    if (n === 1) {
        startInsertPosition = 0;
        endInsertPosition = originalString.indexOf(';');
        if (endInsertPosition === -1) {
            endInsertPosition = originalString.length;
        }
    } else {
        for (let i = 0; i < originalString.length; i++) {
            if (originalString[i] === ';') {
                commaCount++;
                if (commaCount === n - 1 && overwrite) {
                    startInsertPosition = i + 1;
                } else if (commaCount === n) {
                    if (!overwrite) {
                        startInsertPosition = i;
                    }
                    endInsertPosition = i;
                    break;
                }
            }
        }
    }
    if (startInsertPosition !== -1 && endInsertPosition !== -1) {
        return originalString.slice(0, startInsertPosition) + insertString + originalString.slice(endInsertPosition);
    } else {
        return originalString;
    }
}

/**
 * Convertit une chaîne de caractères de rôles en un tableau 2D.
 *
 * Les point-virgules (';') séparent les éléments en différents tableaux de niveau 1.
 * Les virgules (',') séparent les éléments au sein de chaque tableau de niveau 1,
 * formant ainsi des sous-tableaux.
 *
 * @param {string} rolesString - La chaîne de caractères utilisée par la base de données
 *                               pour sauvegarder les informations de rôles.
 * @returns {string[][]} Un tableau 2D où chaque sous-tableau représente un groupe de rôles.
 *
 * Exemple d'utilisation :
 * const originalString = "185,190;32;44,55;666;";
 * const result = rolesStringParser(originalString);
 * result sera [["185", "190"], ["32"], ["44", "55"], ["666"]]
 */
export function rolesStringParser(rolesString: string) {
    return rolesString ? rolesString.split(';').map((role) => role.split(',')) : [];
}

/**
 * Recherche les contacts qui concordent avec les roleIds passés en param
 * @param {any} roleIds - un array contenant les id des contacts associé à un role
 * @param {any} contacts - un array de contacts ou faire la recherche
 * @returns {never[]} Un tableau de dictionnaire de la forme [{nom du contact, id du contact},...]
 *
 */
export function findContacts(roleIds, contacts) {
    return roleIds.flatMap(
        (id) =>
            contacts
                ?.filter((contact) => String(contact.contact.id) === id)
                .map((contact) => ({ name: contact.contact.name, id: String(contact.contact.id) })) || [],
    );
}
